import { useEffect, useRef, useState } from "react";
import { Play, Square } from "lucide-react";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { IconButton } from "../../components/ui/IconButton";
import { useI18n } from "../../i18n/I18nProvider";
import { clampRegion, formatRange, type ClampedRegion } from "./region";
import { mountWaveform, type Waveform } from "./waveform";

export interface RegionEditorProps {
  readonly url: string;
  readonly saved: { readonly start_ms: number; readonly length_ms: number };
  readonly locked: boolean;
  /** The operator fixed the song: dragging is off, but the preview still plays. */
  readonly songLocked?: boolean;
  /** Changes each time a region is saved; a change plays the saved moment. */
  readonly savedMoment: number;
  readonly onDraftChange: (draft: ClampedRegion | null) => void;
}

type WaveStatus = "loading" | "ready" | "too_short" | "error";

const TRANSPORT_ICON_SIZE = 22;
const TRANSPORT_STROKE = 1.75;

/** The neutral zone: readout and play on top, ink bars on ice below with one region between two blue lines. */
export function RegionEditor({
  url,
  saved,
  locked,
  songLocked = false,
  savedMoment,
  onDraftChange,
}: RegionEditorProps) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const waveRef = useRef<Waveform | null>(null);
  const durationRef = useRef<number | null>(null);
  const savedRef = useRef(saved);
  const onDraftRef = useRef(onDraftChange);
  const momentRef = useRef(savedMoment);
  const [status, setStatus] = useState<WaveStatus>("loading");
  const [draft, setDraft] = useState<ClampedRegion | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    onDraftRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    if (momentRef.current === savedMoment) {
      return;
    }
    momentRef.current = savedMoment;
    waveRef.current?.playFill();
  }, [savedMoment]);

  useEffect(() => {
    if (draft) {
      waveRef.current?.annotate(
        t("player.sectionLabel"),
        formatRange(draft.start_ms, draft.end_ms),
      );
    }
  }, [draft, t]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const applyDraft = (next: ClampedRegion) => {
      setDraft(next);
      onDraftRef.current(next);
    };
    const handle = mountWaveform(container, url, {
      onReady: (durationMs) => {
        durationRef.current = durationMs;
        const next = clampRegion(savedRef.current.start_ms, savedRef.current.length_ms, durationMs);
        if (!next.ok) {
          setStatus("too_short");
          onDraftRef.current(null);
          return;
        }
        setStatus("ready");
        handle.setBounds(next);
        applyDraft(next);
      },
      onRegionChange: ({ start_ms, end_ms }) => {
        const duration = durationRef.current;
        if (duration === null) {
          return;
        }
        const next = clampRegion(start_ms, end_ms - start_ms, duration);
        if (!next.ok) {
          return;
        }
        if (next.start_ms !== start_ms || next.end_ms !== end_ms) {
          handle.setBounds(next);
        }
        applyDraft(next);
      },
      onPlayingChange: setPlaying,
      onError: () => setStatus("error"),
    });
    waveRef.current = handle;
    return () => {
      handle.destroy();
      waveRef.current = null;
    };
  }, [url]);

  useEffect(() => {
    waveRef.current?.setEditable(!locked && !songLocked);
  }, [locked, songLocked]);

  const playable = status === "ready" && !locked;
  const playLabel = playing ? t("player.stop") : t("player.play");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-h-16 min-w-0 flex-col">
          {draft ? (
            <>
              <JerseyNumber value={formatRange(draft.start_ms, draft.end_ms)} size="lg" />
              <p className="text-note text-ink-3">
                {t("player.regionLength", { seconds: draft.length_ms / 1000 })}
              </p>
            </>
          ) : null}
        </div>
        <IconButton
          label={playLabel}
          icon={
            playing ? (
              <Square
                aria-hidden="true"
                size={TRANSPORT_ICON_SIZE}
                strokeWidth={TRANSPORT_STROKE}
                fill="currentColor"
              />
            ) : (
              <Play
                aria-hidden="true"
                size={TRANSPORT_ICON_SIZE}
                strokeWidth={TRANSPORT_STROKE}
                fill="currentColor"
              />
            )
          }
          variant="secondary"
          className="size-12! rounded-full!"
          disabled={!playable}
          onClick={() => waveRef.current?.togglePlay()}
        />
      </div>
      <div className="relative min-w-0">
        <div ref={containerRef} className="min-w-0" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 bg-red"
        />
      </div>
      {status === "loading" ? (
        <p className="text-note text-ink-3">{t("player.waveformLoading")}</p>
      ) : null}
      {status === "error" ? (
        <p role="alert" className="text-note font-medium text-red">
          {t("player.waveformError")}
        </p>
      ) : null}
      {status === "too_short" ? (
        <p role="alert" className="text-note font-medium text-red">
          {t("player.songTooShort")}
        </p>
      ) : null}
    </div>
  );
}
